import React, { useState } from 'react';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

import { Modal, Textarea, IOption, Select } from 'shared-components';
import { IMessage, IRegionInterviewConfig } from 'api-paths-cas';

import { IEventType } from '../../../../store/event_types/types';
import { getDisplayName } from '../../../../utilities/formatting';
import { shallowEqual, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';

export interface IProps {
  eventTypes: IEventType[];
  regionInterviewConfig: IRegionInterviewConfig[];
  onSendMessage: (message: IMessage) => void;
  onRequestInterview: (eventType: IEventType, note: string) => void;
  onRequestRecordedInterview: (interviewId: string, note: string) => void;
  onClose: () => void;
}

const RequestNewInterview = (props: IProps) => {
  const {
    eventTypes,
    regionInterviewConfig = [],
    onSendMessage,
    onRequestInterview,
    onRequestRecordedInterview,
    onClose,
  } = props;

  const { auth, config } = useSelector(({ auth, config }: ApplicationState) => ({ auth, config }), shallowEqual);

  const userName = auth.student_number!;
  const displayName = getDisplayName(auth, config);

  const options: IOption[] = [
    {
      label: 'Recorded video interviews',
      value: 'recorded',
      populate: {
        options: regionInterviewConfig?.map(interview => ({
          label: interview.interviewName || '',
          value: interview.interviewId || '',
        })),
      },
    },
    {
      label: 'Interview bookings',
      value: 'notRecorded',
      populate: {
        options: eventTypes
          .filter(item => item.id !== null)
          .map(event => ({ label: event.name ?? '', value: event.id! })),
      },
    },
  ];

  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [note, setNote] = useState('');
  const [isSendToStudent, setIsSendToStudent] = useState(false);

  const onSelectEventType = (value: string) => {
    setSelectedEventType(value);
  };

  const sendMessage = (name: string) => {
    const author = userName || 'Anonymous';
    const message: IMessage = {
      message: `Interview <${name}> Requested${note ? `: ${note}` : ''}`,
      createdAt: Date.now(),
      isSendToStudent,
      messageType: 'note',
      author,
      authorName: displayName || author,
      messageFrom: 'uni',
      messageTo: 'uni',
    };
    onSendMessage(message);
  };

  const onInvite = () => {
    const eventTypeObj = eventTypes.find(event => event.id === selectedEventType)!;
    if (eventTypeObj) {
      sendMessage(eventTypeObj.name || '');
      onRequestInterview(eventTypeObj, note);
      onClose();
      return;
    }
    const recordedInterview = regionInterviewConfig.find(interview => interview.interviewId === selectedEventType);
    if (recordedInterview) {
      sendMessage(recordedInterview.interviewName || '');
      onRequestRecordedInterview(recordedInterview.interviewId, note);
      onClose();
    }
  };

  return (
    <Modal
      id="request-new-interview"
      show={true}
      title={'Invite to new interview'}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={onClose}>Cancel</Button>

          <Button
            id="invite-to-interview-button"
            onClick={onInvite}
            isDisabled={selectedEventType === ''}
            variant={'solid'}
          >
            Invite
          </Button>
        </ButtonGroup>
      }
    >
      <Select
        id={'interview-type'}
        key={'interview-type'}
        label={'Interview'}
        options={options}
        value={selectedEventType}
        placeHolder="Choose interview…"
        onChange={(evt: any) => onSelectEventType(evt.currentTarget.value)}
        mb={10}
      />

      <Textarea
        label={'Add note (optional)'}
        id={'interview-note'}
        value={note}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) => setNote(event.currentTarget.value)}
        mb={3}
      />

      <Checkbox
        id="send-to-student-message"
        isChecked={isSendToStudent}
        onChange={() => setIsSendToStudent(!isSendToStudent)}
        size={'lg'}
      >
        Send this note to student
      </Checkbox>
    </Modal>
  );
};

export default RequestNewInterview;
